import { debounce } from 'lodash';
import { useEffect, useMemo, useState } from 'react';

import { Query, QueryUpdaterCallback } from '@shared/api/types';
import { Icon } from '@shared/ui/components/Icon';

import { FilterConfiguration } from './DataTable.types';
import { DataTableDateFilter } from './DataTableDateFilter';
import { DataTableSelectFilter } from './DataTableSelectFilter';
import { DataTableTextFilter } from './DataTableTextFilter';

type DataTableFilterProps = {
  filter: FilterConfiguration;
  query: Query;
  onQueryChange: QueryUpdaterCallback;
};

export function DataTableFilter({ filter, query, onQueryChange }: DataTableFilterProps) {
  const [internalValue, setInternalValue] = useState<string | number | null>(query.filter[filter.name] ?? null);
  const [filterVisible, setFilterVisible] = useState(Boolean(internalValue));

  useEffect(() => {
    if (internalValue !== query.filter[filter.name]) {
      setInternalValue(query.filter[filter.name] ?? null);
    }
  }, [filter.name, query]);

  useEffect(() => {
    if (Boolean(internalValue)) {
      showFilter();
    }
  }, [internalValue]);

  function showFilter() {
    if (!filterVisible) {
      setFilterVisible(true);
    }
  }

  function hideFilter() {
    if (!Boolean(internalValue)) {
      setFilterVisible(false);
    }
  }

  function clearFilter() {
    onQueryChange(
      (query): Query => ({
        ...query,
        filter: {
          ...query.filter,
          [filter.name]: undefined,
        },
      })
    );

    setFilterVisible(false);
  }

  const debouncedOnChange = useMemo(() => {
    return debounce(
      (value: string | number | null) => {
        onQueryChange(
          (query): Query => ({
            ...query,
            filter: {
              ...query.filter,
              [filter.name]: value ?? undefined,
            },
          })
        );
      },
      500,
      { leading: true, trailing: true, maxWait: 1000 }
    );
  }, [filter.name, onQueryChange]);

  useEffect(() => {
    debouncedOnChange(internalValue);
  }, [internalValue]);

  return (
    <label
      className="border border-gray-100 bg-gray-100 cursor-pointer rounded flex focus-within:border-indigo-400"
      onClick={showFilter}
      onBlur={hideFilter}
    >
      <span className="px-3 py-2 text-gray-600 flex items-center gap-2 whitespace-nowrap">
        <Icon icon={filter.icon ?? 'search'} />
        {filter.label}
      </span>
      {filterVisible && (
        <>
          {filter.type === 'select' && (
            <DataTableSelectFilter filter={filter} value={internalValue} onChange={setInternalValue} />
          )}
          {filter.type === 'date' && (
            <DataTableDateFilter filter={filter} value={internalValue} onChange={setInternalValue} />
          )}
          {(!filter.type || filter.type === 'text') && (
            <DataTableTextFilter value={internalValue} onChange={setInternalValue} />
          )}
          <span className="bg-white p-1 pr-3 flex items-center rounded-r">
            <button className="underline text-xs text-gray-400" onClick={clearFilter}>
              Clear
            </button>
          </span>
        </>
      )}
    </label>
  );
}
